import { useState, useCallback, useRef } from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Image, FileText, Sparkles, X, Loader2, PenLine, Coins, Zap, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { useStudyStreak } from "@/hooks/useStudyStreak";
import { useNavigate } from "react-router-dom";
import ProcessingOptions, { type ProcessingType } from "./ProcessingOptions";
import ProcessingResult from "./ProcessingResult";

type InputMode = "upload" | "write";

const ACCEPTED_TYPES = [
  { label: "PNG", icon: Image, ext: ".png" },
  { label: "JPG", icon: Image, ext: ".jpg/.jpeg" },
  { label: "WEBP", icon: Image, ext: ".webp" },
  { label: "PDF", icon: FileText, ext: ".pdf" },
];

const UploadSection = () => {
  const [inputMode, setInputMode] = useState<InputMode>("upload");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [textInput, setTextInput] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<{ text: string; type: ProcessingType } | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { credits, hasCredits, refetch: refetchCredits } = useCredits();
  const { updateStreak } = useStudyStreak();
  const navigate = useNavigate();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, []);

  const handleFile = async (file: File) => {
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({ title: "File too large", description: "Maximum file size is 15 MB.", variant: "destructive" });
      return;
    }
    const allowed = ["image/png", "image/jpeg", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast({ title: "Unsupported file", description: "Please upload a PNG, JPG, WEBP, or PDF file.", variant: "destructive" });
      return;
    }

    setShowOptions(false);
    setResult(null);
    setIsUploading(true);
    setUploadProgress(0);

    // Simulate upload progress while reading
    const interval = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + 12, 90));
    }, 80);

    try {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        await new Promise<void>((resolve) => {
          reader.onload = (e) => {
            setPreview(e.target?.result as string);
            resolve();
          };
          reader.readAsDataURL(file);
        });
      } else {
        setPreview(null);
      }
      setUploadedFile(file);
      setUploadProgress(100);
    } finally {
      clearInterval(interval);
      setTimeout(() => setIsUploading(false), 400);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setPreview(null);
    setTextInput("");
    setShowOptions(false);
    setResult(null);
  };

  const saveToDatabase = async (resultText: string, type: ProcessingType, sourceName: string) => {
    if (!user) return;

    try {
      const { error } = await supabase.from('processed_notes').insert({
        user_id: user.id,
        file_name: sourceName,
        processing_type: type,
        result: resultText,
      });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };

  const handleProcess = async (type: ProcessingType) => {
    const hasContent = inputMode === "upload" ? uploadedFile : textInput.trim();
    if (!hasContent) return;

    if (!user) {
      toast({ 
        title: "Login required", 
        description: "Please log in to process and save your notes.",
      });
      navigate('/auth');
      return;
    }

    // Check credits before processing (display only - actual deduction happens server-side)
    if (!hasCredits) {
      toast({ 
        title: "No credits remaining", 
        description: "Purchase a plan to get more credits.",
        variant: "destructive"
      });
      // Scroll to pricing section
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setIsProcessing(true);
    try {
      // Handle flashcards separately
      if (type === 'flashcards') {
        let content: string;
        
        if (inputMode === "upload" && uploadedFile) {
          // First extract text from the image
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(uploadedFile);
          });
          
          const { data: extractData, error: extractError } = await supabase.functions.invoke('process-notes', {
            body: { imageBase64: base64, type: 'extract' }
          });
          
          if (extractError) throw extractError;
          if (extractData.error) throw new Error(extractData.error);
          
          content = extractData.result;
        } else {
          content = textInput;
        }

        const title = inputMode === "upload" && uploadedFile ? uploadedFile.name : "Notes Flashcards";
        
        const { data, error } = await supabase.functions.invoke('generate-flashcards', {
          body: { content, title }
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        refetchCredits();
        updateStreak(); // Update study streak
        toast({ 
          title: "Flashcards created!", 
          description: `${data.cardCount} flashcards generated. View them in the Flashcards section.`
        });
        setShowOptions(false);
        navigate('/flashcards');
        return;
      }

      // Handle quiz separately
      if (type === 'quiz') {
        let content: string | undefined;
        let imageBase64: string | undefined;
        
        if (inputMode === "upload" && uploadedFile) {
          imageBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(uploadedFile);
          });
        } else {
          content = textInput;
        }

        const title = inputMode === "upload" && uploadedFile ? uploadedFile.name : "Notes Quiz";
        
        const { data, error } = await supabase.functions.invoke('generate-quiz', {
          body: { content, imageBase64, title }
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        refetchCredits();
        updateStreak(); // Update study streak
        toast({ 
          title: "Quiz created!", 
          description: `${data.questionCount} questions generated. View them in the Quizzes section.`
        });
        setShowOptions(false);
        navigate('/quiz');
        return;
      }

      // Regular processing
      let body: { imageBase64?: string; textContent?: string; type: ProcessingType };

      if (inputMode === "upload" && uploadedFile) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(uploadedFile);
        });
        body = { imageBase64: base64, type };
      } else {
        body = { textContent: textInput, type };
      }

      const { data, error } = await supabase.functions.invoke('process-notes', {
        body
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      setResult({ text: data.result, type });
      setShowOptions(false);
      
      const sourceName = inputMode === "upload" && uploadedFile ? uploadedFile.name : "Written Notes";
      await saveToDatabase(data.result, type, sourceName);
      
      // Refetch credits and update streak after successful processing
      refetchCredits();
      updateStreak();
      
      toast({ title: "Processing complete!", description: "Your notes have been processed and saved." });
    } catch (error: any) {
      console.error('Processing error:', error);
      toast({
        title: "Processing failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const hasInput = inputMode === "upload" ? uploadedFile : textInput.trim().length > 0;

  return (
    <section id="upload" className="py-24 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-accent/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            <Zap className="w-4 h-4" />
            AI-Powered Processing
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-foreground mb-4">
            Upload Your Notes
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-lg">
            Upload an image or PDF of your notes, or type them directly — 
            our AI transforms them into study gold.
          </p>
          
          {/* Credits Display */}
          {user && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary">
              <Coins className="w-4 h-4" />
              <span className="font-medium">{credits} credits remaining</span>
            </div>
          )}
        </div>

        <div className="max-w-2xl mx-auto relative z-10">
          {/* Mode Toggle */}
          {!result && (
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-2xl bg-muted/80 backdrop-blur-sm p-1.5 shadow-soft border border-border/30">
                <button
                  onClick={() => { setInputMode("upload"); setShowOptions(false); }}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    inputMode === "upload"
                      ? "bg-background text-foreground shadow-card"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Upload
                </button>
                <button
                  onClick={() => { setInputMode("write"); setShowOptions(false); }}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    inputMode === "write"
                      ? "bg-background text-foreground shadow-card"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <PenLine className="w-4 h-4" />
                  Write
                </button>
              </div>
            </div>
          )}

          {result ? (
            <div className="glass rounded-2xl p-6 shadow-card">
              <ProcessingResult 
                result={result.text} 
                type={result.type} 
                onBack={clearUpload} 
              />
            </div>
          ) : inputMode === "upload" ? (
            !uploadedFile ? (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-3xl p-16 transition-all duration-500 cursor-pointer group ${
                  isDragging
                    ? "border-primary bg-primary/5 scale-[1.01] shadow-glow"
                    : "border-border/60 hover:border-primary/40 hover:bg-primary/[0.02] hover:shadow-soft hover:scale-[1.005]"
                }`}
              >
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  onChange={handleFileInput}
                  aria-label="Upload notes file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                <div className="text-center">
                  <div className={`w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center transition-all duration-500 ${
                    isDragging ? "gradient-primary animate-pulse-glow scale-110" : "bg-primary/10 group-hover:bg-primary/15 group-hover:scale-105"
                  }`}>
                    <Upload className={`w-9 h-9 transition-transform duration-300 ${isDragging ? "text-primary-foreground -translate-y-1" : "text-primary group-hover:-translate-y-0.5"}`} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    {isDragging ? "Drop your notes here!" : "Drag & drop your notes"}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    or click to browse files
                  </p>
                  
                  <div className="flex items-center justify-center gap-3 flex-wrap text-sm text-muted-foreground">
                    {ACCEPTED_TYPES.map(({ label, icon: Icon }) => (
                      <span key={label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/60 border border-border/30">
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground/60 mt-4">Max file size: 15 MB</p>

                  {isUploading && (
                    <div className="mt-6 max-w-xs mx-auto space-y-1.5">
                      <Progress value={uploadProgress} className="h-2" />
                      <p className="text-xs text-muted-foreground">
                        {uploadProgress < 100 ? "Reading file…" : "Ready!"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="glass rounded-3xl p-8 shadow-card border border-border/30">
                <div className="flex items-start gap-4 mb-6">
                  {preview && (
                    <img 
                      src={preview} 
                      alt="Uploaded notes" 
                      className="w-28 h-28 object-cover rounded-2xl border border-border/50 shadow-soft"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-foreground truncate">
                        {uploadedFile.name}
                      </h3>
                      <button
                        onClick={clearUpload}
                        className="p-1 hover:bg-muted rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-muted-foreground" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>

                {!showOptions ? (
                  <Button 
                    variant="hero" 
                    className="w-full"
                    onClick={() => setShowOptions(true)}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Process with AI
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-center text-muted-foreground">
                      What would you like to do?
                    </p>
                    <ProcessingOptions 
                      onSelect={handleProcess} 
                      disabled={isProcessing}
                    />
                    {isProcessing && (
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm">Processing your notes...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="glass rounded-3xl p-8 shadow-card border border-border/30">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-foreground">
                    Type or paste your notes
                  </label>
                  {textInput && (
                    <button
                      onClick={clearUpload}
                      className="p-1 hover:bg-muted rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
                <Textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Enter your notes here... You can paste content from anywhere or type directly."
                  className="min-h-[200px] resize-none"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {textInput.length} characters
                </p>
              </div>

              {!showOptions ? (
                <Button 
                  variant="hero" 
                  className="w-full"
                  onClick={() => setShowOptions(true)}
                  disabled={isProcessing || !textInput.trim()}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Process with AI
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-center text-muted-foreground">
                    What would you like to do?
                  </p>
                  <ProcessingOptions 
                    onSelect={handleProcess} 
                    disabled={isProcessing}
                  />
                  {isProcessing && (
                    <div className="flex items-center justify-center gap-2 text-primary">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Processing your notes...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default UploadSection;
